import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bookingsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const statusColors = {
  pending: { bg: '#FFF7ED', color: '#D97706', label: 'Pending' },
  confirmed: { bg: '#EFF6FF', color: '#1D4ED8', label: 'Confirmed' },
  in_progress: { bg: '#F0FDF4', color: '#16A34A', label: 'In Progress' },
  completed: { bg: '#F0FDF4', color: '#16A34A', label: 'Completed' },
  cancelled: { bg: '#FEF2F2', color: '#DC2626', label: 'Cancelled' },
};

const CustomerDashboard = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [reviewModal, setReviewModal] = useState(null);
  const [review, setReview] = useState({ rating: 5, comment: '' });

  useEffect(() => {
    bookingsAPI.getMy().then(res => { setBookings(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = activeTab === 'all' ? bookings : bookings.filter(b => b.status === activeTab);

  const submitReview = async () => {
    try {
      await bookingsAPI.addReview(reviewModal, review);
      setReviewModal(null);
      const res = await bookingsAPI.getMy();
      setBookings(res.data);
    } catch (err) { alert('Failed to submit review'); }
  };

  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === 'pending').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    spent: bookings.filter(b => b.status === 'completed').reduce((s, b) => s + b.totalAmount, 0),
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1>Welcome back, {user?.name?.split(' ')[0]}! 👋</h1>
            <p>Manage your bookings and service history</p>
          </div>
          <Link to="/services" className="new-booking-btn">+ Book New Service</Link>
        </div>

        {/* Stats */}
        <div className="stats-row">
          <div className="stat-card"><div className="stat-num">{stats.total}</div><div className="stat-lbl">Total Bookings</div></div>
          <div className="stat-card pending"><div className="stat-num">{stats.pending}</div><div className="stat-lbl">Pending</div></div>
          <div className="stat-card success"><div className="stat-num">{stats.completed}</div><div className="stat-lbl">Completed</div></div>
          <div className="stat-card blue"><div className="stat-num">Rs. {stats.spent.toLocaleString()}</div><div className="stat-lbl">Total Spent</div></div>
        </div>

        {/* Tabs */}
        <div className="dash-tabs">
          {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(tab => (
            <button key={tab} className={`dash-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Bookings */}
        {loading ? <div className="dash-loading">Loading your bookings...</div> :
          filtered.length === 0 ? (
            <div className="dash-empty">
              <div className="empty-icon">📋</div>
              <h3>No bookings found</h3>
              <p>You haven't made any bookings yet</p>
              <Link to="/services" className="new-booking-btn">Browse Services</Link>
            </div>
          ) : (
            <div className="bookings-list">
              {filtered.map(booking => {
                const s = statusColors[booking.status] || statusColors.pending;
                return (
                  <div key={booking._id} className="booking-item">
                    <div className="bi-left">
                      <div className="bi-avatar">{booking.provider?.name?.charAt(0) || 'P'}</div>
                    </div>
                    <div className="bi-body">
                      <div className="bi-top">
                        <div>
                          <div className="bi-service">{booking.service?.title}</div>
                          <div className="bi-provider">Provider: {booking.provider?.name}</div>
                          <div className="bi-meta">
                            📅 {new Date(booking.bookingDate).toLocaleDateString()} &nbsp;•&nbsp;
                            🕐 {booking.timeSlot} &nbsp;•&nbsp;
                            📍 {booking.city}
                          </div>
                        </div>
                        <div className="bi-right">
                          <span className="bi-status" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                          <div className="bi-amount">Rs. {booking.totalAmount?.toLocaleString()}</div>
                          <div className="bi-commission">Platform fee: Rs. {booking.commissionAmount?.toFixed(0)}</div>
                        </div>
                      </div>
                    {booking.status === 'pending' && (
  <button
    onClick={async () => {
      if (window.confirm('Are you sure you want to cancel this booking?')) {
        await bookingsAPI.updateStatus(booking._id, 'cancelled');
        const res = await bookingsAPI.getMy();
        setBookings(res.data);
      }
    }}
    style={{
      marginTop: '10px',
      padding: '7px 18px',
      background: 'white',
      color: '#DC2626',
      border: '1.5px solid #DC2626',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    }}
    onMouseEnter={e => {
      e.target.style.background = '#FEE2E2';
    }}
    onMouseLeave={e => {
      e.target.style.background = 'white';
    }}
  >
 Cancel Booking
  </button>
)}
                      {booking.status === 'completed' && !booking.review?.rating && (
                        <button className="review-btn" onClick={() => setReviewModal(booking._id)}>⭐ Leave a Review</button>
                      )}
                      {booking.review?.rating && (
                        <div className="review-shown">Your review: {'★'.repeat(booking.review.rating)} — {booking.review.comment}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {/* Review Modal */}
      {reviewModal && (
        <div className="modal-overlay" onClick={() => setReviewModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Leave a Review</h3>
            <div className="star-select">
              {[1,2,3,4,5].map(n => (
                <span key={n} className={`star ${review.rating >= n ? 'active' : ''}`} onClick={() => setReview({ ...review, rating: n })}>★</span>
              ))}
            </div>
            <textarea placeholder="Share your experience..." rows={4} value={review.comment}
              onChange={(e) => setReview({ ...review, comment: e.target.value })} />
            <div className="modal-actions">
              <button onClick={() => setReviewModal(null)} className="modal-cancel">Cancel</button>
              <button onClick={submitReview} className="modal-submit">Submit Review</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
